import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useBusiness } from "./use-business";
import { endOfDay, format, startOfDay, subDays, subMonths } from "date-fns";
import { convertCurrencyAmount } from "@/lib/fx-rates";

type StatusBucket = { status: string; count: number };
type SplitBucket = { label: string; count: number };

export type CateringAnalyticsData = {
  // KPIs
  orders7d: number;
  orders30d: number;
  revenue7d: number;
  revenue30d: number;
  activeCustomers30d: number;
  deliveryOrders30d: number;
  pickupOrders30d: number;

  // Trends (shared UI expects these keys for the toggle area)
  weekdayTrendsWeek: Array<{ day: string; count: number }>;
  monthlyTrends6Months: Array<{ month: string; count: number }>;

  // Orders analytics
  ordersOverTime14d: Array<{ date: string; count: number; revenue: number }>;
  statusDistribution30d: StatusBucket[];
  fulfillmentSplit30d: SplitBucket[];
  topItems: Array<{ item: string; qty: number; revenue: number }>;
  topCustomers: Array<{ customer: string; contact: string; orders: number; revenue: number }>;

  // Export
  exportOrders: Array<{
    created_at: string;
    confirmation_code: string;
    customer_name: string;
    customer_phone: string;
    customer_email: string;
    fulfillment: "delivery" | "pickup";
    status: string;
    total_amount: number;
    currency: string;
  }>;
  exportOrderItems: Array<{
    created_at: string;
    confirmation_code: string;
    item_name: string;
    quantity: number;
    unit_price: number;
    line_total: number;
  }>;
};

const DEFAULT_DATA: CateringAnalyticsData = {
  orders7d: 0,
  orders30d: 0,
  revenue7d: 0,
  revenue30d: 0,
  activeCustomers30d: 0,
  deliveryOrders30d: 0,
  pickupOrders30d: 0,
  weekdayTrendsWeek: [],
  monthlyTrends6Months: [],
  ordersOverTime14d: [],
  statusDistribution30d: [],
  fulfillmentSplit30d: [],
  topItems: [],
  topCustomers: [],
  exportOrders: [],
  exportOrderItems: [],
};

export function useCateringAnalytics() {
  const { business } = useBusiness();
  const [data, setData] = useState<CateringAnalyticsData>(DEFAULT_DATA);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAnalytics = useCallback(async () => {
    if (!business?.id) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);

    try {
      const now = new Date();
      const since30 = subDays(now, 30);
      const since7 = subDays(now, 7);
      const since14 = subDays(now, 14);
      const since6m = subMonths(now, 6);

      const { data: orders, error: oErr } = await (supabase as any)
        .from("food_orders")
        .select(
          `
          id,
          customer_id,
          created_at,
          confirmation_code,
          status,
          currency,
          total_amount,
          employee_name,
          customer_phone,
          customer_email,
          delivery_address,
          delivery_place_id,
          customer:customers(
            id,
            name,
            email,
            phone
          )
        `,
        )
        .eq("business_id", business.id)
        .gte("created_at", since30.toISOString())
        .order("created_at", { ascending: false })
        .limit(5000);
      if (oErr) throw oErr;

      const ordersArr = orders || [];
      const orderIds = ordersArr.map((o: any) => String(o.id || "")).filter(Boolean);

      let itemsArr: any[] = [];
      if (orderIds.length > 0) {
        // Fetch items for the same order window (ensures item stats match order stats).
        const { data: items, error: iErr } = await (supabase as any)
          .from("food_order_items")
          .select(
            `
            id,
            created_at,
            order_id,
            item_name,
            unit_price,
            quantity
          `,
          )
          .eq("business_id", business.id)
          .in("order_id", orderIds)
          .order("created_at", { ascending: false })
          .limit(20000);
        if (iErr) throw iErr;
        itemsArr = items || [];
      }

      const inRange = (iso: string, start: Date, end: Date) => {
        const t = new Date(iso).getTime();
        return t >= start.getTime() && t <= end.getTime();
      };

      const currency = (business as any)?.currency || "GHS";
      const convertRevenue = (amount: number, sourceCurrency?: string | null) =>
        convertCurrencyAmount(amount, sourceCurrency || currency, currency);

      const orders7dArr = ordersArr.filter((o: any) => o.created_at && inRange(o.created_at, since7, now));
      const orders7d = orders7dArr.length;
      const orders30d = ordersArr.length;
      const revenue7d = orders7dArr.reduce(
        (sum: number, o: any) => sum + convertRevenue(Number(o.total_amount || 0), o.currency),
        0,
      );
      const revenue30d = ordersArr.reduce(
        (sum: number, o: any) => sum + convertRevenue(Number(o.total_amount || 0), o.currency),
        0,
      );

      const isDelivery = (o: any) => !!String(o.delivery_address || "").trim() || !!String(o.delivery_place_id || "").trim();
      const deliveryOrders30d = ordersArr.filter(isDelivery).length;
      const pickupOrders30d = Math.max(0, orders30d - deliveryOrders30d);

      // Active customers (30d) based on phone/email fallback (customer_id is present but not selected here for anon safety)
      const customerKey = (o: any) => {
        const cid = o?.customer_id ? String(o.customer_id) : "";
        if (cid) return `id:${cid}`;
        const p = String(o.customer_phone || "").trim();
        const e = String(o.customer_email || "").trim().toLowerCase();
        if (p) return `p:${p}`;
        if (e) return `e:${e}`;
        const n = String(o.employee_name || "").trim().toLowerCase();
        return n ? `n:${n}` : "";
      };
      const customerSet = new Set<string>();
      for (const o of ordersArr) {
        const k = customerKey(o);
        if (k) customerSet.add(k);
      }

      // 14-day orders trend (daily)
      const ordersOverTime14d: Array<{ date: string; count: number; revenue: number }> = [];
      for (let i = 13; i >= 0; i--) {
        const d = subDays(now, i);
        const start = startOfDay(d);
        const end = endOfDay(d);
        const dayOrders = ordersArr.filter((o: any) => o.created_at && inRange(o.created_at, start, end));
        ordersOverTime14d.push({
          date: format(d, "MMM d"),
          count: dayOrders.length,
          revenue: dayOrders.reduce(
            (sum: number, o: any) => sum + convertRevenue(Number(o.total_amount || 0), o.currency),
            0,
          ),
        });
      }

      // Status distribution (30d)
      const statusCounts = new Map<string, number>();
      for (const o of ordersArr) {
        const s = String(o.status || "new");
        statusCounts.set(s, (statusCounts.get(s) || 0) + 1);
      }
      const statusDistribution30d = Array.from(statusCounts.entries())
        .map(([status, count]) => ({ status, count }))
        .sort((a, b) => b.count - a.count);

      const fulfillmentSplit30d: SplitBucket[] = [
        { label: "delivery", count: deliveryOrders30d },
        { label: "pickup", count: pickupOrders30d },
      ];

      // Top items (30d) from cart items table
      const orderCurrencyById = new Map<string, string>();
      for (const order of ordersArr) {
        if (order?.id) orderCurrencyById.set(String(order.id), String(order.currency || currency));
      }
      const itemAgg = new Map<string, { item: string; qty: number; revenue: number }>();
      for (const it of itemsArr) {
        const name = String(it.item_name || "Item");
        const qty = Number(it.quantity || 0);
        const unit = Number(it.unit_price || 0);
        const sourceCurrency = orderCurrencyById.get(String(it.order_id || "")) || currency;
        const rev = convertRevenue(qty * unit, sourceCurrency);
        const row = itemAgg.get(name) || { item: name, qty: 0, revenue: 0 };
        row.qty += qty;
        row.revenue += rev;
        itemAgg.set(name, row);
      }
      const topItems = Array.from(itemAgg.values()).sort((a, b) => b.qty - a.qty).slice(0, 10);

      // Top customers (30d)
      const custAgg = new Map<string, { customer: string; contact: string; orders: number; revenue: number }>();
      for (const o of ordersArr) {
        const key = customerKey(o);
        if (!key) continue;
        const cust = (o as any)?.customer || null;
        const nameRaw = String(cust?.name || o.employee_name || "Customer").trim();
        const email = String(cust?.email || o.customer_email || "").trim();
        const phone = String(cust?.phone || o.customer_phone || "").trim();
        const displayName = nameRaw && !nameRaw.includes("@") ? nameRaw : "Customer";
        const contact = phone || email;
        const row = custAgg.get(key) || { customer: displayName, contact, orders: 0, revenue: 0 };
        row.orders += 1;
        row.revenue += convertRevenue(Number(o.total_amount || 0), o.currency);
        // Prefer keeping a contact if we didn't have one
        if (!row.contact && contact) row.contact = contact;
        // Prefer keeping a longer/more descriptive name
        if (displayName.length > row.customer.length) row.customer = displayName;
        custAgg.set(key, row);
      }
      const topCustomers = Array.from(custAgg.values()).sort((a, b) => b.orders - a.orders).slice(0, 10);

      // Weekday trends (last 8 weeks cumulative, based on submissions)
      const weekdayLabels = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
      const weekdayIndex = (d: Date) => (d.getDay() + 6) % 7; // Mon=0..Sun=6
      const weekdayCounts = new Array(7).fill(0);
      const cumulativeStart = subDays(now, 56);
      for (const o of ordersArr) {
        const dt = o.created_at ? new Date(o.created_at) : null;
        if (!dt) continue;
        if (dt < cumulativeStart) continue;
        weekdayCounts[weekdayIndex(dt)] += 1;
      }
      const weekdayTrendsWeek = weekdayLabels.map((day, i) => ({ day, count: weekdayCounts[i] }));

      // Monthly trends (last 6 months) based on submissions
      const monthlyTrends6Months: Array<{ month: string; count: number }> = [];
      for (let i = 5; i >= 0; i--) {
        const d = subMonths(now, i);
        const key = format(d, "MMM");
        const start = startOfDay(new Date(d.getFullYear(), d.getMonth(), 1));
        const end = endOfDay(new Date(d.getFullYear(), d.getMonth() + 1, 0));
        const count = ordersArr.filter((o: any) => o.created_at && inRange(o.created_at, start, end)).length;
        monthlyTrends6Months.push({ month: key, count });
      }

      // Exports (orders + order items)
      const exportOrders = ordersArr.slice(0, 5000).map((o: any) => ({
        created_at: o.created_at || "",
        confirmation_code: String(o.confirmation_code || ""),
        customer_name: String(o.employee_name || ""),
        customer_phone: String(o.customer_phone || ""),
        customer_email: String(o.customer_email || ""),
        fulfillment: isDelivery(o) ? ("delivery" as const) : ("pickup" as const),
        status: String(o.status || "new"),
        total_amount: Number(o.total_amount || 0),
        currency: String(o.currency || currency),
      }));
      const orderIdToCode = new Map<string, string>();
      for (const o of ordersArr) {
        if (o?.id) orderIdToCode.set(String(o.id), String(o.confirmation_code || ""));
      }
      const exportOrderItems = itemsArr.slice(0, 20000).map((it: any) => {
        const qty = Number(it.quantity || 0);
        const unit = Number(it.unit_price || 0);
        const code = orderIdToCode.get(String(it.order_id || "")) || "";
        return {
          created_at: it.created_at || "",
          confirmation_code: code,
          item_name: String(it.item_name || ""),
          quantity: qty,
          unit_price: unit,
          line_total: qty * unit,
        };
      });

      setData({
        orders7d,
        orders30d,
        revenue7d,
        revenue30d,
        activeCustomers30d: customerSet.size,
        deliveryOrders30d,
        pickupOrders30d,
        weekdayTrendsWeek,
        monthlyTrends6Months,
        ordersOverTime14d,
        statusDistribution30d,
        fulfillmentSplit30d,
        topItems,
        topCustomers,
        exportOrders,
        exportOrderItems,
      });
    } catch (e: any) {
      setError(e?.message || "Failed to load catering analytics");
    } finally {
      setLoading(false);
    }
  }, [business?.currency, business?.id]);

  // Load on mount + whenever business changes
  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  return { data, loading, error, refetch: fetchAnalytics };
}


